export default {
  id: "clients",
  title: "Clients",
  section: "People",
  order: 70,
  keywords: "client details notes goals medications documents CST intake PID authorizations create timeline",
  body: `
## What it is

The Clients page lists everyone served; each client's Details page is the
hub for their information, schedule, authorizations, notes, goals,
medications, CST, and documents. Creating a client is deliberately minimal
— first name, last name, payer type — everything else is configured on the
Details page afterward.

## Details worth keeping accurate

- **DSPD PID** — required for DSPD billing exports (timesheet billing,
  EVV, quarterly submissions). Keep the leading zeros.
- **Address** — used for maps and the clock-in distance check on community
  codes. The address field offers Google autocomplete and a map pin
  picker; make sure the address has a map point (geocode).
- **Payer type, office, support coordinator, counselor** — drive which
  reports, alerts, and emails include the client, and where quarterly
  reports get sent.
- **Timezone** — matters for clients outside the agency's home timezone;
  shift times follow it.

## The Notes timeline

The Notes view merges every note source into one chronological timeline:

- standalone client notes,
- schedule notes,
- shift notes from clock-outs,
- per-client notes on group shifts,
- and each shift's **question answers and goal answers**, nested under the
  shift they belong to.

What caregivers must write differs by service type — DSPD group shifts
document per client, DSPD 1-on-1 uses questions/goals/medications, VR uses
activities and questions — so some shifts carry a free-text note and
others carry structured answers. Both count as documentation.

## CST (Client Support Toolkit)

The CST tab holds the client's current CST packet — the document
caregivers acknowledge before working with the client.

**Build it in-app**: create/edit in the CST editor, then **publish** —
publishing supersedes the previous version and prompts caregivers to
acknowledge the new one.

**Or upload an existing PDF**: click **Upload CST** on the CST tab.

1. The app reads the PDF — including scanned/boxed layouts, which go
   through OCR — into a **draft** CST plus its medication list.
2. Review the draft in the editor: OCR is good but not perfect, and the
   upload reports anything it wasn't sure about as warnings.
3. Publish when it's right; the same supersede-and-acknowledge flow runs.

## Goals & medications

- **Goals** linked to the client appear at clock-out for the relevant
  service codes; the answers land in the Notes timeline and feed quarterly
  reports.
- **Medications** set up on the client generate the medication
  administration section at clock-out (each med marked Given with a time
  or Skipped with a reason) — and the log is reviewable on approval.

## Documents

The Documents tab stores intake forms, CST packets, authorization PDFs,
signed compliance forms, and anything uploaded for the client. Intake PDFs
can be uploaded and parsed into the client record the same way CSTs are.
`,
};
